import React from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useLanguage, useT } from "@/lib/LanguageContext";
import * as crmApi from "../api/crmApi";
import { crmErrorMessage } from "../api/crmErrors";
import { fmtCount } from "../format";
import useActiveImport from "../hooks/useActiveImport";
import { datedFilename, downloadBlob } from "../../../lib/download";
import CrmButton from "../ui/CrmButton";
import { FOCUS_RING } from "../ui/classes";
import { CRM } from "../../../constants/testIds/crm";

/**
 * The Clients screen's import banner (spec Screen 5): "Importing 400 of 1,240…" while the
 * background import runs, then "Import complete — 1,187 new, 38 merged, 15 skipped" with
 * "Download skipped rows" (each row + reason).
 */
export default function ImportBanner() {
  const t = useT("crm");
  const tc = useT("common");
  const { lang } = useLanguage();
  const { job, running, dismiss } = useActiveImport();
  if (!job) return null;
  const n = (v) => fmtCount(v, lang);

  const download = async () => {
    try {
      downloadBlob(await crmApi.downloadSkippedRows(job.id), datedFilename("skipped-rows", "csv"));
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc, "errors.exportFailed"));
    }
  };

  const text = running
    ? t("import.bannerRunning", { processed: n(job.progress?.processed), total: n(job.progress?.total ?? job.row_count) })
    : job.status === "failed"
      ? t("import.bannerFailed")
      : t("import.bannerDone", { new: n(job.result?.created), merged: n(job.result?.merged), skipped: n(job.result?.skipped) });

  return (
    <div data-testid={CRM.clientsImportBanner} role="status"
      className="relative flex flex-wrap items-center gap-x-4 gap-y-2 rounded-full bg-eh-sage/20 px-5 py-3 pr-12 text-sm text-eh-ink">
      <span><strong>{t("import.bannerLabel")}</strong> {text}</span>
      {running && (
        <span className="h-1.5 w-40 overflow-hidden rounded-full bg-white/70" aria-hidden="true">
          <span className="block h-full bg-eh-sage-deep" style={{ width: `${Math.min(100, Math.round(((job.progress?.processed || 0) / Math.max(1, job.progress?.total || job.row_count || 1)) * 100))}%` }} />
        </span>
      )}
      {!running && (job.result?.skipped || 0) > 0 && (
        <CrmButton variant="link" size="sm" className="font-body font-normal text-eh-ink underline" onClick={download} data-testid={CRM.clientsImportSkipped}>
          {t("import.downloadSkipped")}
        </CrmButton>
      )}
      {!running && (
        <button type="button" onClick={() => dismiss().catch((e) => toast.error(crmErrorMessage(e, t, tc)))} aria-label={t("common.dismiss")} data-testid={CRM.clientsImportDismiss}
          className={`absolute right-4 top-1/2 -translate-y-1/2 rounded-full p-1 hover:bg-eh-sage/30 ${FOCUS_RING}`}>
          <X size={14} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
